/**
 * Shape adapters for the Schedule rules and for the strategies the
 * Calendar cross-references.
 *
 * The rule shape barely moves — the mock was already written against the
 * table PRD 08 describes — so this is mostly about the fields the server
 * owns and about giving the Calendar a strategy shape it can match on.
 *
 * Pure module: no React, no fetch. Unit-tested with `node --test`.
 */

const WEEKDAYS = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun']

const num = (v) => (v === null || v === undefined || v === '' ? null : Number(v))

/**
 * A clock time as the server's `^HH:MM$` wants it, or `null`.
 *
 * `<input type="time">` is not one control but one per browser: Safari
 * hands back `08:00:00` once a `step` is set, Firefox hands back `''`
 * while a field is half-typed, Chrome gives `HH:MM`. The server pattern
 * is strict and its 422 reads like a regex (Thibault, 2026-10-02,
 * "window_start String should match pattern…"), so the seconds are
 * dropped here and anything that is not a time becomes `null` for the
 * caller to fall back on.
 *
 * A one-digit hour (`6:00`, or the `6:00:` the server used to send for
 * any window before 10:00) is padded rather than refused: a rule saved
 * before that server fix still has to open and save.
 */
export const hhmm = (v) => {
	const m = /^(\d{1,2}):([0-5]\d)(?::.*)?$/.exec(String(v ?? '').trim())
	if (!m || Number(m[1]) > 23) return null
	return `${m[1].padStart(2, '0')}:${m[2]}`
}

export const rules = {
	toServer: (r) => ({
		name: r.name,
		priority: num(r.priority) ?? 50,
		is_active: r.is_active !== false,
		mode: r.mode === 'fixed' ? 'fixed' : 'rolling',
		dba_from: r.mode === 'fixed' ? null : num(r.dba_from),
		dba_to:   r.mode === 'fixed' ? null : num(r.dba_to),
		fixed_from: r.mode === 'fixed' ? (r.fixed_from || null) : null,
		fixed_to:   r.mode === 'fixed' ? (r.fixed_to || null) : null,
		frequency_kind: r.frequency_kind,
		frequency_value: r.frequency_kind === 'specific_hours'
			? (r.frequency_value || []).map(hhmm).filter(Boolean)
			: num(r.frequency_value) ?? 1,
		window_start: hhmm(r.window_start) || '08:00',
		window_end: hhmm(r.window_end) || '20:00',
		weekdays: (r.weekdays || []).filter(d => WEEKDAYS.includes(d)),
		office_codes: (r.office_codes || []).filter(Boolean),
		pickup_hour: hhmm(r.pickup_hour),
		dropoff_hour: hhmm(r.dropoff_hour),
	}),
	fromServer: (row) => ({ ...row }),
}

/**
 * A strategy as the Calendar's matcher wants it.
 *
 * The matcher asks four things of a strategy: is it live, which pickup
 * dates does it cover, on which weekdays, and for which offices and
 * ACRISS codes. The canonical strategy expresses the first three through
 * `period` (exactly one of seasons / lead times / weekdays is filled) and
 * the rest through its scope lists.
 *
 * Absent means "everything" on every axis — the same reading the server's
 * overlap algebra uses, so the calendar does not disagree with the 409 the
 * operator just saw.
 */
export function strategyForCalendar(row) {
	const period = row.period || {}
	const season = (period.seasons || [])[0] || null
	const lead = (period.leadTimes || [])[0] || null
	const weekdayNumbers = period.weekdays || []

	return {
		id: row.id,
		name: row.name,
		priority: row.priority,
		is_active: row.status === 'enabled',
		// The canonical season is MM-DD (it repeats every year); the
		// calendar compares full dates, so the year is filled in by the
		// matcher, not invented here.
		season_from: season ? season.seasonFrom : null,
		season_to: season ? season.seasonTo : null,
		dba_from: lead ? lead.leadFrom : 0,
		dba_to: lead ? lead.leadTo : 365,
		weekdays: weekdayNumbers.length
			? weekdayNumbers.map(n => WEEKDAYS[(Number(n) - 1 + 7) % 7])
			: [...WEEKDAYS],
		office_codes: row.filterGroupOffices || [],
		sipp_codes: row.compareByCategory ? [] : (row.filterAcriss || []),
	}
}
